import React from "react";
import {
  CalendarDays,
  History,
  LoaderCircle,
  RefreshCcw,
} from "lucide-react";
import {
  buildReportActionCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME,
  DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import { publishDispatchCaptureArrival } from "../lib/dispatchCaptureStatusEvents.js";
import { fetchReportActionCaptureHistory } from "../lib/systemConfigApi.js";
import {
  formatCaptureActivityGroupLabel,
  groupCaptureItemsByActivityDate,
} from "../utils/dispatchCaptureListGroups.js";
import {
  formatTimestamp,
  ImportContextItem,
  resolveActorLabel,
} from "./SollioUniagOrderImportHistory.jsx";
import { joinClasses, statusToneClasses } from "./resultsPrimitives/shared.js";

const PAGE_SIZE = 25;
const SECONDARY_BUTTON_CLASS_NAME = "inline-flex min-h-9 items-center justify-center gap-2 rounded-[4px] border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
const EMAIL_STATUS_PRESENTATION = {
  failed: { label: "Échec", tone: "danger" },
  skipped: { label: "Aucun envoi", tone: "warning" },
  succeeded: { label: "Envoyé", tone: "success" },
};

function normalizeHistoryPayload(payload, fallbackPage) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const page = Number.isInteger(Number(payload?.page)) && Number(payload.page) > 0
    ? Number(payload.page)
    : fallbackPage;
  const pageSize = Number.isInteger(Number(payload?.page_size)) && Number(payload.page_size) > 0
    ? Number(payload.page_size)
    : PAGE_SIZE;
  const total = Number.isFinite(Number(payload?.total)) && Number(payload.total) >= 0
    ? Math.trunc(Number(payload.total))
    : items.length;
  return {
    hasMore: payload?.has_more === true,
    items,
    page,
    pageSize,
    total,
  };
}

export function resolveReportActionKindLabel(action) {
  const normalizedAction = typeof action === "string" ? action.trim() : "";
  if (normalizedAction.endsWith("_email")) {
    return "Courriel";
  }
  if (normalizedAction.includes("xlsx")) {
    return "XLSX";
  }
  return normalizedAction.endsWith("_pdf") || normalizedAction.endsWith("_pdf_download") ? "PDF" : "Rapport";
}

function formatDispatchDate(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return "Non applicable";
  }
  const [year, month, day] = normalizedValue.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function ReportActionHistoryCard({ item, isNew = false }) {
  const timestamp = formatTimestamp(item?.created_at);
  const actionLabel = typeof item?.action_label === "string" && item.action_label.trim()
    ? item.action_label.trim()
    : "Action Rapports";
  const summary = typeof item?.summary === "string"
    ? item.summary.split(" · ")
      .filter((part) => part.trim() !== `dispatch ${item?.dispatch_date}`)
      .join(" · ").trim()
    : "";
  const kindLabel = resolveReportActionKindLabel(item?.action);
  const emailStatus = EMAIL_STATUS_PRESENTATION[item?.status] || null;

  return (
    <article
      className={joinClasses([
        "grid min-w-0 gap-1.5 rounded-[4px] border border-black/6 bg-white px-2.5 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
        isNew ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME : "",
      ])}
      data-dispatch-capture-arrival={isNew ? "true" : undefined}
      data-report-action-capture-card="true"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <strong className="min-w-0 break-words text-[0.86rem] tracking-[-0.02em] text-slate-950">
            {actionLabel}
          </strong>
          <p className="m-0 flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[0.62rem] font-medium leading-4 text-slate-500">
            <span className="inline-flex min-w-0 items-baseline gap-1.5">
              <span className="text-slate-400">Généré</span>
              <time
                className="whitespace-nowrap text-[0.92rem] font-bold leading-none tabular-nums text-slate-950"
                dateTime={timestamp.isoValue}
                title={timestamp.dateTimeLabel}
              >
                {timestamp.timeLabel}
              </time>
            </span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {emailStatus ? (
            <span
              className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em]",
                statusToneClasses[emailStatus.tone],
              ])}
              data-report-action-status={item.status}
            >
              {emailStatus.label}
            </span>
          ) : null}
          <span
            className={joinClasses([
              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em]",
              statusToneClasses.neutral,
            ])}
          >
            {kindLabel}
          </span>
        </div>
      </div>

      <div
        className="grid min-w-0 grid-cols-2 gap-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,2.2fr)]"
        data-report-action-capture-details-row="true"
      >
        <ImportContextItem label="Utilisateur" value={resolveActorLabel(item)} wrap />
        <ImportContextItem label="Date de dispatch" value={formatDispatchDate(item?.dispatch_date)} wrap />
        <div className="col-span-2 grid min-w-0 lg:col-span-1">
          <ImportContextItem label="Détail" value={summary || actionLabel} wrap />
        </div>
      </div>
    </article>
  );
}

export default function ReportActionCaptureHistory() {
  const [page, setPage] = React.useState(1);
  const [historyState, setHistoryState] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const requestSequenceRef = React.useRef(0);

  const loadHistory = React.useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!quiet) {
      setIsLoading(true);
      setLoadError("");
    }
    try {
      const payload = normalizeHistoryPayload(
        await fetchReportActionCaptureHistory({ page, pageSize: PAGE_SIZE }),
        page,
      );
      if (requestSequence === requestSequenceRef.current) {
        setHistoryState(payload);
        setLoadError("");
      }
    } catch (error) {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setLoadError(error instanceof Error ? error.message : "Impossible de charger l’historique des rapports.");
      }
    } finally {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setIsLoading(false);
      }
    }
  }, [page]);

  React.useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    let intervalId = null;
    const refreshQuietly = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        void loadHistory({ quiet: true });
      }
    };
    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        intervalId = window.setInterval(refreshQuietly, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loadHistory]);

  const items = historyState?.items || [];
  const reportKeys = React.useMemo(() => items.map(buildReportActionCaptureKey), [items]);
  const highlightedKeys = useDispatchCaptureArrivalHighlight(reportKeys, {
    enabled: historyState != null && historyState.page === page,
    resetKey: page,
    onArrival: () => publishDispatchCaptureArrival("reports"),
    view: "reports",
  });
  const total = historyState?.total || 0;
  const currentPage = historyState?.page || page;
  const pageSize = historyState?.pageSize || PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const groupedItems = React.useMemo(
    () => groupCaptureItemsByActivityDate(items, (item) => item?.created_at),
    [items],
  );

  return (
    <div className="grid min-w-0 gap-3" data-report-action-capture-history="true">
      <section className="grid gap-2 rounded-[4px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
            statusToneClasses.success,
          ])}>
            Lecture seule
          </span>
          <span className="text-[0.8rem] font-medium text-slate-600">
            {total} rapport{total > 1 ? "s" : ""} dans l’historique
          </span>
        </div>
        <div className="grid gap-0.5 rounded-[4px] border border-slate-200 bg-slate-50/70 px-3 py-2">
          <h2 className="m-0 text-[0.86rem] font-bold text-slate-950">Rapports</h2>
          <p className="m-0 text-[0.72rem] leading-5 text-slate-600">
            Historique cumulé des PDF, fichiers XLSX et courriels générés depuis le Centre de rapports et les automatisations.
          </p>
        </div>
      </section>

      {loadError ? (
        <section className="grid gap-3 rounded-[4px] border border-rose-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]" role="alert">
          <p className="m-0 text-[0.9rem] font-semibold text-rose-700">{loadError}</p>
          <div>
            <button className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => void loadHistory()} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      ) : isLoading && !historyState ? (
        <section className="flex min-h-52 items-center justify-center gap-3 rounded-[4px] border border-black/6 bg-white text-[0.86rem] font-medium text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
          Chargement des rapports…
        </section>
      ) : items.length > 0 ? (
        <section className="grid max-w-full min-h-min min-w-0 gap-6 overflow-x-clip" aria-label="Historique des rapports">
          {groupedItems.map((group) => (
            <div
              className="grid max-w-full min-h-min min-w-0 gap-2 overflow-x-clip rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5"
              data-report-action-capture-date-group="true"
              key={group.activityDate || "unspecified"}
            >
              <header className="flex items-center justify-between gap-3 rounded-[4px] border border-slate-300 bg-slate-200/90 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
                <div className="flex min-w-0 items-center gap-2 text-[0.95rem] font-extrabold tracking-[-0.02em] text-slate-950">
                  <CalendarDays className="shrink-0 text-slate-700" size={16} aria-hidden="true" />
                  <h3 className="m-0 truncate">{formatCaptureActivityGroupLabel(group.activityDate)}</h3>
                </div>
                <span className="shrink-0 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-[0.68rem] font-bold text-slate-600">
                  {group.items.length} rapport{group.items.length > 1 ? "s" : ""}
                </span>
              </header>
              {group.items.map((item) => (
                <ReportActionHistoryCard
                  item={item}
                  isNew={highlightedKeys.has(buildReportActionCaptureKey(item))}
                  key={item.audit_id}
                />
              ))}
            </div>
          ))}
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-[4px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="grid max-w-lg gap-2">
            <History className="mx-auto text-slate-400" size={30} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-900">Aucun rapport à afficher</p>
            <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
              Les rapports générés, téléchargés ou envoyés par courriel apparaîtront ici.
            </p>
          </div>
        </section>
      )}

      {total > 0 ? (
        <nav className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]" aria-label="Pagination des rapports">
          <span className="text-[0.76rem] font-medium text-slate-600">
            Page {currentPage} sur {pageCount} · {total} rapport{total > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              className={SECONDARY_BUTTON_CLASS_NAME}
              disabled={isLoading || currentPage <= 1}
              onClick={() => setPage((currentValue) => Math.max(1, currentValue - 1))}
              type="button"
            >
              Précédente
            </button>
            <button
              className={SECONDARY_BUTTON_CLASS_NAME}
              disabled={isLoading || !historyState?.hasMore}
              onClick={() => setPage((currentValue) => currentValue + 1)}
              type="button"
            >
              Suivante
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
